import { useEffect, useState, useCallback } from "react";
import { Gift, Plus, Trash2, Trophy, Loader2, Save, Edit, X, ImagePlus, Users } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, uploadImage, formatApiErrorDetail } from "../lib/api";

// Format ISO -> local datetime-local input value (YYYY-MM-DDTHH:mm)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

const EMPTY_FORM = { title: "", prize: "", description: "", image_url: "", ends_at: "" };

function GiveawayForm({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial ? {
    title: initial.title || "",
    prize: initial.prize || "",
    description: initial.description || "",
    image_url: initial.image_url || "",
    ends_at: isoToLocalInput(initial.ends_at),
  } : EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Nur Bilder erlaubt.");
      return;
    }
    setUploading(true);
    try {
      const up = await uploadImage(f);
      setForm((p) => ({ ...p, image_url: up.url }));
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.prize || !form.ends_at) {
      toast.error("Bitte Titel, Gewinn und Ende-Datum ausfüllen.");
      return;
    }
    const ends = new Date(form.ends_at);
    if (ends <= new Date()) {
      toast.error("Ende-Datum muss in der Zukunft liegen.");
      return;
    }
    onSave({
      title: form.title.trim(),
      prize: form.prize.trim(),
      description: form.description.trim() || null,
      image_url: form.image_url || null,
      ends_at: localInputToIso(form.ends_at),
    });
  };

  return (
    <form onSubmit={submit} className="bg-[#FFFFFF] border border-[#1D5BFF]/30 rounded-2xl p-5 space-y-4" data-testid="giveaway-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#4DBFFF]" /> {initial ? "Verlosung bearbeiten" : "Neue Verlosung"}
        </h3>
        <button type="button" onClick={onCancel} className="p-2 hover:bg-slate-100/70 rounded-full" data-testid="cancel-giveaway-form">
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Titel *</label>
        <input
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          placeholder="z. B. Tankgutschein-Verlosung"
          maxLength={120}
          data-testid="giveaway-title-input"
          className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF]"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Gewinn *</label>
        <input
          value={form.prize}
          onChange={(e) => setForm((p) => ({ ...p, prize: e.target.value }))}
          placeholder="z. B. 100€ Shell-Gutschein"
          maxLength={200}
          data-testid="giveaway-prize-input"
          className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF]"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Beschreibung</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          placeholder="Was gibt's zu gewinnen? Wie läuft die Verlosung?"
          maxLength={1500}
          rows={3}
          data-testid="giveaway-description-input"
          className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] resize-y"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Endet am *</label>
        <input
          type="datetime-local"
          value={form.ends_at}
          onChange={(e) => setForm((p) => ({ ...p, ends_at: e.target.value }))}
          data-testid="giveaway-ends-at-input"
          className="w-full bg-[#F5F8FD] border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 focus:outline-none focus:border-[#1D5BFF]"
        />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-600 block mb-1">Bild (optional)</label>
        <div className="flex items-center gap-3">
          {form.image_url && (
            <img src={fileUrl(form.image_url)} alt="Vorschau" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
          )}
          <label className="cursor-pointer bg-[#F5F8FD] border border-slate-200 hover:border-[#1D5BFF]/50 rounded-xl px-4 py-2.5 text-sm text-slate-700 flex items-center gap-2">
            <ImagePlus className="w-4 h-4" />
            {uploading ? "Lädt…" : (form.image_url ? "Bild ändern" : "Bild hochladen")}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="giveaway-image-input" />
          </label>
          {form.image_url && (
            <button type="button" onClick={() => setForm((p) => ({ ...p, image_url: "" }))} className="text-xs text-red-400 hover:text-red-300">
              entfernen
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={busy || uploading}
          data-testid="save-giveaway-btn"
          className="bg-[#1D5BFF] hover:bg-[#1546C9] disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 shadow-[0_8px_30px_rgba(0,102,255,0.35)]"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {initial ? "Speichern" : "Verlosung starten"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-slate-100/70 hover:bg-slate-100 text-slate-700 font-semibold px-5 py-2.5 rounded-full"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function EntriesList({ giveawayId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/giveaways/${giveawayId}/entries`);
        setEntries(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [giveawayId]);
  if (loading) return <div className="text-xs text-slate-500 p-3">Lade Teilnehmer…</div>;
  if (entries.length === 0) return <div className="text-xs text-slate-500 p-3">Noch keine Teilnehmer.</div>;
  return (
    <div className="max-h-48 overflow-y-auto bg-[#F5F8FD] rounded-lg p-2 space-y-1" data-testid={`entries-list-${giveawayId}`}>
      {entries.map((e) => (
        <div key={e.user_id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-100/70 rounded">
          <img src={fileUrl(e.user_avatar)} alt={e.user_name} className="w-6 h-6 rounded-full object-cover border border-slate-200" />
          <span className="text-sm text-slate-700">{e.user_name}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminGiveaways() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/giveaways");
      setList(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (payload) => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/giveaways/${editing.id}`, payload);
        toast.success("Verlosung aktualisiert.");
      } else {
        await api.post("/admin/giveaways", payload);
        toast.success("Verlosung gestartet 🎉");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Verlosung "${g.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/giveaways/${g.id}`);
      toast.success("Gelöscht.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    }
  };

  const drawWinner = async (g) => {
    if (!window.confirm(`Jetzt zufällig einen Gewinner ziehen und im Chat ankündigen?`)) return;
    try {
      const { data } = await api.post(`/admin/giveaways/${g.id}/draw`);
      toast.success(`Gewinner: ${data.winner_name} 🏆`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    }
  };

  return (
    <div data-testid="admin-giveaways" className="space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="text-xs sm:text-sm text-slate-600 flex-1 min-w-0">
          Erstelle Verlosungen, lege Preis & Ende fest. Mitglieder können beitreten – Gewinner wird zufällig gezogen.
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="new-giveaway-btn"
            className="bg-[#1D5BFF] hover:bg-[#1546C9] text-white font-semibold px-4 py-2 rounded-full flex items-center gap-2 shrink-0 text-sm"
          >
            <Plus className="w-4 h-4" /> Neue Verlosung
          </button>
        )}
      </div>

      {showForm && (
        <GiveawayForm
          initial={editing}
          onSave={save}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          busy={busy}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Verlosungen…
        </div>
      ) : list.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-slate-200/60 rounded-2xl p-10 text-center text-slate-500 text-sm">
          Noch keine Verlosungen. Klick auf &bdquo;Neue Verlosung&ldquo;.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((g) => {
            const isExpanded = expandedId === g.id;
            return (
              <div key={g.id} className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-4" data-testid={`admin-giveaway-${g.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  {g.image_url && <img src={fileUrl(g.image_url)} alt={g.title} className="w-16 h-16 rounded-lg object-cover border border-slate-200" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-bold text-slate-900 truncate">{g.title}</h4>
                      <StatusPill status={g.status} />
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">🎁 {g.prize}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {g.entries_count} Teilnehmer</span>
                      <span>endet {new Date(g.ends_at).toLocaleString("de-DE")}</span>
                    </div>
                    {g.winner_name && (
                      <div className="text-xs text-emerald-300 mt-1 flex items-center gap-1">
                        <Trophy className="w-3 h-3" /> Gewinner: {g.winner_name}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : g.id)}
                      data-testid={`toggle-entries-${g.id}`}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100/70 hover:bg-slate-100 text-slate-700 flex items-center gap-1"
                    >
                      <Users className="w-3.5 h-3.5" /> {isExpanded ? "Ausblenden" : "Teilnehmer"}
                    </button>
                    {g.status === "ended" && (
                      <button
                        onClick={() => drawWinner(g)}
                        data-testid={`draw-winner-${g.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/20 hover:bg-emerald-500 hover:text-slate-900 text-emerald-300 border border-emerald-500/40 flex items-center gap-1"
                      >
                        <Trophy className="w-3.5 h-3.5" /> Gewinner ziehen
                      </button>
                    )}
                    {!g.winner_user_id && (
                      <button
                        onClick={() => { setEditing(g); setShowForm(true); }}
                        data-testid={`edit-giveaway-${g.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100/70 hover:bg-slate-100 text-slate-700 flex items-center gap-1"
                      >
                        <Edit className="w-3.5 h-3.5" /> Bearbeiten
                      </button>
                    )}
                    <button
                      onClick={() => remove(g)}
                      data-testid={`delete-giveaway-${g.id}`}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold bg-red-500/15 hover:bg-red-500 hover:text-slate-900 text-red-300 border border-red-500/30 flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Löschen
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-3"><EntriesList giveawayId={g.id} /></div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "drawn") return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Gezogen</span>;
  if (status === "ended") return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Beendet · Ziehung offen</span>;
  return <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1D5BFF]/20 text-[#4DBFFF]">Aktiv</span>;
}
